import { useGqlQuery } from '@API/useAvQuery';
import { AsyncGetOptions } from '@fabric-ui/components/Table';
import useQueryObjectSql from './useQueryObjectSql';
import useQuerySql from './useQuerySql';

export const useAsyncFilterOptions = ({
  options,
  isEnabled,
  placeholderData,
}: {
  options?: ReturnType<AsyncGetOptions>;
  isEnabled: boolean;
  placeholderData?: <T>(previousData: T | undefined) => T | undefined;
}) => {
  const { type, ...hookOptions } = options || {};

  const {
    data: gqlData,
    isLoading: gqlIsLoading,
    isPending: gqlIsPending,
    isPlaceholderData: gqlIsPlaceholderData,
  } = useGqlQuery({
    ...(hookOptions as any),
    enabled: isEnabled && (type === 'gql' || type === 'gqlPagination'),
    placeholderData,
  });

  const {
    data: sqlData,
    isLoading: sqlIsLoading,
    isPending: sqlIsPending,
    isPlaceholderData: sqlIsPlaceholderData,
    totals: sqlTotals,
  } = useQuerySql({
    ...(hookOptions as any),
    options: {
      enabled: isEnabled && type === 'sql',
      placeholderData,
    },
  });

  const {
    data: queryObjectData,
    isLoading: queryObjectIsLoading,
    isPending: queryObjectIsPending,
    isPlaceholderData: queryObjectIsPlaceholderData,
    totalRowCount: queryObjectTotalRowCount,
    errors: queryObjectErrors,
  } = useQueryObjectSql({
    ...(hookOptions as any),
    options: {
      enabled: isEnabled && type === 'queryObject',
      placeholderData,
    },
  });

  // `isLoading` is `isPending && isFetching`, so it is false on the render where the query becomes enabled but
  // has not started fetching yet - and false again while placeholder data from a previous query key is served.
  // At both of those moments `data` does not describe the current inputs, which matters to callers that read
  // meaning into a value's *absence*. This says "the data in hand was produced by the query we're asking about".
  const isCurrentData = (isPending: boolean, isPlaceholderData: boolean) => !isPending && !isPlaceholderData;

  if (type === 'gql') {
    return {
      data: gqlData,
      isLoading: gqlIsLoading,
      totalRowCount: undefined,
      isCurrentData: isCurrentData(gqlIsPending, gqlIsPlaceholderData),
    };
  }

  if (type === 'gqlPagination') {
    const { data = undefined, totals = undefined } = (gqlData || {}) as any;
    return { data, isLoading: gqlIsLoading, totalRowCount: totals, isCurrentData: isCurrentData(gqlIsPending, gqlIsPlaceholderData) };
  }
  if (type === 'sql') {
    return {
      data: sqlData,
      isLoading: sqlIsLoading,
      totalRowCount: sqlTotals,
      isCurrentData: isCurrentData(sqlIsPending, sqlIsPlaceholderData),
    };
  }
  if (type === 'queryObject') {
    return {
      data: queryObjectData,
      isLoading: queryObjectIsLoading,
      totalRowCount: queryObjectTotalRowCount,
      isCurrentData: isCurrentData(queryObjectIsPending, queryObjectIsPlaceholderData),
      errors: queryObjectErrors,
    };
  }

  // No query at all, so there is no fetched list to reason about.
  return { data: undefined, isLoading: false, totalRowCount: undefined, isCurrentData: false };
};
